import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { useWorkshopBlocker, FLOW } from "../../logic/useWorkshopBlocker";
import { supabase } from "../../logic/client";


const STORAGE_PREFIX = "shareInvitation";
const CACHE_TTL = 1000 * 60 * 30; // 30 min session

export default function ShareInvitation(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const blocker = useWorkshopBlocker();
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [notice, setNotice] = React.useState<string | null>(null);

  const [email1, setEmail1] = React.useState("");
  const [email2, setEmail2] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const [submitted, setSubmitted] = React.useState(false);

  const [employeeId, setEmployeeId] = React.useState<string | null>(null);

  const myIndex = FLOW.indexOf("ShareInvitation");
  const blockerIndex = FLOW.indexOf(blocker);
  const canContinue = blockerIndex > myIndex;

  function handleContinue() {
    if (canContinue) {
      handlePageChange(FLOW[myIndex + 1]);
      return;
    }
    setNotice("ENG / DE");
  }

  function normalize(email: string) {
    return email.trim().toLowerCase();
  }

  function isValidEmail(email: string) {
    return /\S+@\S+\.\S+/.test(email);
  }

  const loadLocal = React.useCallback((employeeId: string) => {
    const key = `${STORAGE_PREFIX}_${employeeId}`;
    const saved = localStorage.getItem(key);

    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);

      if (
        !parsed ||
        !Array.isArray(parsed.emails) ||
        typeof parsed.timestamp !== "number"
      ) {
        localStorage.removeItem(key);
        return;
      }

      if (Date.now() - parsed.timestamp > CACHE_TTL) {
        localStorage.removeItem(key);
        return;
      }

      setEmail1(parsed.emails[0] ?? "");
      setEmail2(parsed.emails[1] ?? "");
      setSubmitted(true);
    } catch (e) {
      console.error("LOCAL PARSE ERROR", e);
      localStorage.removeItem(key);
    }
  }, []);

  const loadContext = React.useCallback(async () => {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: employee, error } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    if (error || !employee) return;

    setEmployeeId(employee.id);
    loadLocal(employee.id);
  }, [loadLocal]);

  React.useEffect(() => {
    loadContext();
  }, [loadContext]);

  async function handleSend() {
    if (loading || !employeeId) return;

    setLoading(true);

    try {
      const emails = [email1, email2]
        .map(normalize)
        .filter((e) => e && isValidEmail(e));

      if (emails.length === 0) {
        throw new Error("No valid emails");
      }

      const rows = emails.map((email) => ({
        employee_id: employeeId,
        invited_email: email,
      }));

      const { error } = await supabase
        .from("workshop_invitation_requests")
        .insert(rows);

      if (error) throw error;

      localStorage.setItem(
        `${STORAGE_PREFIX}_${employeeId}`,
        JSON.stringify({
          emails,
          timestamp: Date.now(),
        })
      );

      setEmail1(emails[0] ?? "");
      setEmail2(emails[1] ?? "");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert(isDE ? "Fehler beim Speichern" : "Error saving invitations");
    } finally {
      setLoading(false);
    }
  }

  function handleChangeEmails() {
    setSubmitted(false);
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/invite.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper sx={paperStyle}>
        <Stack spacing={4} alignItems="center">
          <Typography sx={titleStyle}>
            {isDE ? "VIELEN DANK" : "THANK YOU"}
          </Typography>

          <Typography sx={textMain}>
            {isDE
              ? "Resilienz ist steuerbar. Für Menschen, Unternehmen und Regionen."
              : "Resilience is manageable. For individuals, companies, and regions."}
          </Typography>

          <Typography sx={textSub}>
            {isDE
              ? "Hast du weitere Ideen? Welche Menschen oder Unternehmen sollten diesen Weg gehen?"
              : "Do you have ideas? Who should take this path as well?"}
          </Typography>

          {!submitted ? (
            <>
              <Stack spacing={2} sx={{ width: "100%", maxWidth: 420 }}>
                <TextField
                  placeholder={isDE ? "E-Mail Adresse 1" : "Email address 1"}
                  value={email1}
                  onChange={(e) => setEmail1(e.target.value)}
                  fullWidth
                  sx={inputStyle}
                />

                <TextField
                  placeholder={isDE ? "E-Mail Adresse 2" : "Email address 2"}
                  value={email2}
                  onChange={(e) => setEmail2(e.target.value)}
                  fullWidth
                  sx={inputStyle}
                />
              </Stack>

              <Button
                onClick={handleSend}
                disabled={loading || (!email1 && !email2)}
                sx={buttonStyle}
              >
                {loading
                  ? isDE ? "Speichert..." : "Saving..."
                  : isDE ? "Einladungen speichern" : "Save invitations"}
              </Button>
            </>
          ) : (
            <Stack spacing={2} alignItems="center">
              <Typography
                sx={{
                  mt: 3,
                  color: "#fff",
                  fontSize: { xs: "1rem", sm: "1.2rem" },
                  fontWeight: 700,
                  textAlign: "center",
                  maxWidth: 600,
                  lineHeight: 1.6,
                }}
              >
                {isDE
                  ? "Vielen Dank für deinen Beitrag, 1 Million Menschen vorzubereiten."
                  : "Thank you for your contribution to prepare 1 million people."}
              </Typography>

              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>
                {[email1, email2].filter(Boolean).join(" · ")}
              </Typography>

              <Button
                variant="outlined"
                onClick={handleChangeEmails}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.4)",
                  fontWeight: 700,
                  "&:hover": {
                    borderColor: "#fff",
                    background: "rgba(255,255,255,0.05)",
                  },
                }}
              >
                {isDE ? "E-Mails ändern / hinzufügen" : "Change / add emails"}
              </Button>

              <Button
                variant="contained"
                onClick={handleContinue}
                sx={{
                  mt: 1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 800,
                  px: 3,
                  bgcolor: canContinue ? "#ffffff" : "rgba(255,255,255,0.35)",
                  color: canContinue ? "#111827" : "rgba(255,255,255,0.8)",
                  "&:hover": {
                    bgcolor: canContinue ? "#f3f4f6" : "rgba(255,255,255,0.35)",
                  },
                }}
              >
                {isDE ? "Workshop beenden" : "Finish Workshop"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setNotice(null)} severity="info" sx={{ width: "100%", fontWeight: 700 }}>
          {isDE
            ? "Die Workshop-Leitung wird diesen Schritt freigeben, oder bitte beantworte die Frage vollständig und sende sie zuerst ab."
            : "The workshop holder will enable this step, or please complete and submit your answer first."}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* STYLES */

const paperStyle = {
  width: "min(900px, 90vw)",
  borderRadius: 5,
  p: { xs: 4, sm: 6, md: 7 },
  border: "1px solid rgba(255,255,255,0.14)",
  background:
    "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
  boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
  textAlign: "center",
};

const titleStyle = {
  color: "#fff",
  fontSize: { xs: "1.4rem", sm: "1.9rem" },
  fontWeight: 800,
};

const textMain = {
  color: "rgba(255,255,255,0.85)",
  fontSize: { xs: "0.95rem", sm: "1.1rem" },
};

const textSub = {
  color: "rgba(255,255,255,0.7)",
  fontSize: { xs: "0.9rem", sm: "1.05rem" },
};

const buttonStyle = {
  mt: 2,
  px: 4,
  py: 1.5,
  fontWeight: 800,
  borderRadius: 3,
  background: "#e40505",
  color: "#fff",
  "&:hover": { background: "#a10707" },
  "&.Mui-disabled": {
    background: "#777",
    color: "rgba(255,255,255,0.75)",
  },
};

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    color: "#fff",
    background: "rgba(255,255,255,0.05)",
  },
};